import {DateTime} from 'luxon';
import React from 'react';
import styled, {css} from 'styled-components';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {SimpleDropdownWithHeaderProps} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownWithHeader';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {CalendarWeekDaysEnum} from '../../../models/api/calendarCommonTypes';
import {StartAndEndDatesWithPeriod} from '../../../models/dates/dateIntervalModel';
import {FilterDropdownBox} from '../filters/filterDropdownBox/filterDropdownBox';
import {DateRangeComboBoxDropdown} from './dateRangeComboBoxDropdown';

/*
 * Types.
 */

enum DirectionsEnum {
  FORWARD = 'forward',
  BACKWARD = 'backward',
}

interface DateRangeComboBoxProps {
  selectedStartDate?: DateTime;
  selectedEndDate?: DateTime;
  onDateSelect: HandlerOf<StartAndEndDatesWithPeriod>;
  calendarWeekStartDay: CalendarWeekDaysEnum;
  placement?: SimpleDropdownWithHeaderProps['placement'];
  onOpen?: Handler;
  // If true, the period selection chevrons are displayed within the
  // main date filter button rather than as separate buttons.
  periodSelectionChevronsInButton?: boolean;
}

interface IconStyledProps {
  $rotate?: string;
  $margin?: string;
}

/*
 * Styles.
 */

const Wrap = styled.div`
  display: flex;
  align-items: center;
`;

const DropdownOpener = styled(Wrap)`
  min-width: 225px;
  white-space: nowrap;
`;

const StyledStartDate = styled.div`
  text-align: left;
  padding: 0 5px 0 8px;
`;

const StyledEndDate = styled.div`
  text-align: right;
  padding: 0 0 0 5px;
  margin-left: auto;
`;

const iconSharedStyles = (props: IconStyledProps) => css`
  color: ${(p) => p.theme.greys.shade70};
  transform: rotate(${props.$rotate});
  margin: ${props.$margin};
`;

const GreyIcon = styled(Icon)<IconStyledProps>`
  ${iconSharedStyles}
`;

const GreyIconButton = styled(IconButton)<IconStyledProps>`
  ${iconSharedStyles}
`;

const toggleIconSharedStyled = css`
  height: 30px;
  padding-top: 6.5px;
`;

const StyledLeftToggleIconButton = styled(IconButton)`
  ${toggleIconSharedStyled};
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
  border-right: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledRightToggleIconButton = styled(IconButton)`
  ${toggleIconSharedStyled};
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
  border-left: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledDropdownBoxCoordinator = styled(DropdownBoxCoordinator)`
  padding: 0;
`;

/*
 * Component.
 */

export const DateRangeComboBox = (props: DateRangeComboBoxProps) => {
  const theme = useUserTheme();
  const onPopoverToggle = (isOpen: boolean) => {
    if (!isOpen) {
      props.onOpen?.();
    }
  };

  const onPeriodChange = (direction: DirectionsEnum) => () => {
    const {selectedStartDate, selectedEndDate} = props;
    if (!selectedStartDate || !selectedEndDate) {
      return;
    }

    const newPeriod = calculateNextPeriods(selectedStartDate, selectedEndDate, direction);
    props.onDateSelect({startDate: newPeriod.startDate, endDate: newPeriod.endDate});
  };

  const renderButton = () => (
    <DropdownOpener data-testid="dropdownButton">
      <GreyIcon name="calendarSmall" />
      <StyledStartDate data-testid="selectedStartDate">
        {props.selectedStartDate?.toFormat('MMM d, yyyy') || 'mm/dd/yyyy'}
      </StyledStartDate>
      <GreyIcon name="arrowSmall" $rotate="-90deg" $margin="0 0 0 auto" />
      <StyledEndDate data-testid="selectedEndDate">
        {props.selectedEndDate?.toFormat('MMM d, yyyy') || 'mm/dd/yyyy'}
      </StyledEndDate>
    </DropdownOpener>
  );

  const renderDropdown = (popoverTransferProps: PopoverTransferProps) => {
    const onDateSelect = (dates: StartAndEndDatesWithPeriod, closeDropdown = false) => {
      props.onDateSelect(dates);
      if (closeDropdown) {
        popoverTransferProps.onRequestClose();
      }
    };

    return (
      <DateRangeComboBoxDropdown
        {...props}
        popoverTransferProps={popoverTransferProps}
        onDateSelect={onDateSelect}
        onCancel={popoverTransferProps.onRequestClose}
        startDate={props.selectedStartDate}
        endDate={props.selectedEndDate}
      />
    );
  };

  if (props.periodSelectionChevronsInButton) {
    return (
      <Wrap>
        <span data-testid="previousPeriod">
          <StyledLeftToggleIconButton
            iconName="chevronLeftSmall"
            onClick={onPeriodChange(DirectionsEnum.BACKWARD)}
            label="Toggle date range back"
            backgroundColor={theme.greys.shade20}
            backgroundHoverColor={theme.greys.shade30}
          />
        </span>
        <span data-testid="date-range-combo-box-popover">
          <FilterDropdownBox
            renderButton={renderButton}
            renderDropdown={renderDropdown}
            onPopoverToggle={onPopoverToggle}
            noBorderRadius
          />
        </span>
        <span data-testid="nextPeriod">
          <StyledRightToggleIconButton
            iconName="chevronRightSmall"
            onClick={onPeriodChange(DirectionsEnum.FORWARD)}
            label="Toggle date range forward"
            backgroundColor={theme.greys.shade20}
            backgroundHoverColor={theme.greys.shade30}
          />
        </span>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <span data-testid="previousPeriod">
        <GreyIconButton
          iconName="chevronRightSmall"
          $rotate="180deg"
          $margin="0 5px 0 0"
          onClick={onPeriodChange(DirectionsEnum.BACKWARD)}
          label=""
        />
      </span>
      <span data-testid="date-range-combo-box-popover">
        <StyledDropdownBoxCoordinator
          value={renderButton()}
          renderDropdown={renderDropdown}
          onPopoverToggle={onPopoverToggle}
        />
      </span>
      <span data-testid="nextPeriod">
        <GreyIconButton
          iconName="chevronRightSmall"
          onClick={onPeriodChange(DirectionsEnum.FORWARD)}
          label=""
          $margin="0 0 0 5px"
        />
      </span>
    </Wrap>
  );
};

/*
 * Helpers.
 */

function calculateNextPeriods(startDate: DateTime, endDate: DateTime, direction: DirectionsEnum) {
  // Period is full months (last month, last quarter, last year).
  if (
    // This is checking if these dates are the first/last days of months.
    startDate.startOf('month').equals(startDate.startOf('day')) &&
    endDate.endOf('month').equals(endDate.endOf('day'))
  ) {
    // Shift by one more month than the floor of the difference to get to the correct position. This
    // works because if `startDate` is the beginning of the month and `endDate` is the end of the
    // same month the "months diff" will be <1, so flooring and adding 1 will get us the correct shift.
    const {months} = endDate.diff(startDate, ['months']);
    const monthsShift = Math.floor(months) + 1;
    if (direction === DirectionsEnum.BACKWARD) {
      return {
        startDate: startDate.minus({months: monthsShift}),
        endDate: endDate.minus({months: monthsShift}).endOf('month'),
      };
    }
    return {
      startDate: startDate.plus({months: monthsShift}),
      endDate: endDate.plus({months: monthsShift}).endOf('month'),
    };
  }

  // Similar shifting logic as above applies to the days here.
  const {days} = endDate.diff(startDate, ['days']);
  const daysShift = Math.floor(days) + 1;

  if (direction === DirectionsEnum.BACKWARD) {
    return {
      startDate: startDate.minus({days: daysShift}),
      endDate: endDate.minus({days: daysShift}),
    };
  }
  return {startDate: startDate.plus({days: daysShift}), endDate: endDate.plus({days: daysShift})};
}
